import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { resolveActiveDateFnsLocale } from '@/i18n/dateFnsLocale';
import { Icon } from '@/components/common/Icon';
import { cn } from '@/lib/utils';

const TIME_PATTERN = /^\d{2}:\d{2}$/;
const DEFAULT_INTERVAL_MINUTES = 15;
const MINUTES_IN_DAY = 24 * 60;

interface TimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  intervalMinutes?: number;
}

function buildDateFromTime(value: string): Date | null {
  if (TIME_PATTERN.test(value) === false) {
    return null;
  }
  const timeParts = value.split(':');
  const hours = Number(timeParts[0]);
  const minutes = Number(timeParts[1]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return new Date(2000, 0, 1, hours, minutes);
}

function toTimeValue(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');
}

function buildTimeOptions(intervalMinutes: number): string[] {
  const options: string[] = [];
  for (let totalMinutes = 0; totalMinutes < MINUTES_IN_DAY; totalMinutes += intervalMinutes) {
    options.push(toTimeValue(totalMinutes));
  }
  return options;
}

export function TimePicker({
  id,
  value,
  onChange,
  placeholder,
  hasError = false,
  intervalMinutes = DEFAULT_INTERVAL_MINUTES,
}: TimePickerProps) {
  const locale = resolveActiveDateFnsLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const timeOptions = buildTimeOptions(intervalMinutes);
  const selectedDate = buildDateFromTime(value);

  useEffect(
    function closeOnOutsideInteraction() {
      if (isOpen === false) {
        return;
      }
      function handlePointer(pointerEvent: MouseEvent) {
        if (
          containerRef.current !== null &&
          containerRef.current.contains(pointerEvent.target as Node) === false
        ) {
          setIsOpen(false);
        }
      }
      function handleKey(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          setIsOpen(false);
        }
      }
      window.addEventListener('mousedown', handlePointer);
      window.addEventListener('keydown', handleKey);
      return function cleanup() {
        window.removeEventListener('mousedown', handlePointer);
        window.removeEventListener('keydown', handleKey);
      };
    },
    [isOpen]
  );

  useEffect(
    function scrollSelectedIntoView() {
      if (isOpen === true && selectedOptionRef.current !== null) {
        selectedOptionRef.current.scrollIntoView({ block: 'center' });
      }
    },
    [isOpen]
  );

  function formatTimeLabel(timeValue: string): string {
    const timeDate = buildDateFromTime(timeValue);
    if (timeDate === null) {
      return timeValue;
    }
    return format(timeDate, 'p', { locale });
  }

  function selectTime(timeValue: string) {
    onChange(timeValue);
    setIsOpen(false);
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={function toggle() {
          setIsOpen(isOpen === false);
        }}
        className={cn(
          'w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-zoooffwhite border rounded-xl text-left transition-all focus:outline-none focus:border-zooprimary focus:ring-2 focus:ring-zooprimary/40',
          hasError === true ? 'border-zooerrorborder ring-1 ring-zooerrorborder' : 'border-zooborder'
        )}
      >
        <span
          className={selectedDate === null ? 'text-zoodisabledtext text-sm' : 'text-zooindigo text-sm'}
        >
          {selectedDate === null
            ? placeholder === undefined
              ? ''
              : placeholder
            : formatTimeLabel(value)}
        </span>
        <Icon name="Clock" className="text-zoomuted shrink-0" />
      </button>

      {isOpen === true ? (
        <div className="absolute z-30 mt-2 w-full min-w-[7rem] max-h-60 overflow-y-auto bg-zoowhite border border-zooborder rounded-2xl shadow-xl p-1">
          {timeOptions.map(function renderOption(timeValue) {
            const isSelected = timeValue === value;
            return (
              <button
                key={timeValue}
                ref={isSelected === true ? selectedOptionRef : undefined}
                type="button"
                onClick={function pick() {
                  selectTime(timeValue);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                  isSelected === true
                    ? 'bg-zooprimary text-zoowhite font-bold'
                    : 'text-zooindigo hover:bg-zoovpalelavender'
                )}
              >
                {formatTimeLabel(timeValue)}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
